// @ts-nocheck
/**
 * Transformations between the API's Title-Case shareholder enums (`Shareholder.ExclusionType` per
 * `enum-string-title-case` rule) and the short UI codes used inside the Vote Room (`'dqi'`, `'318'`).
 * Also exposes the once-per-analysis loader and the `prepared-for-voting` shape factory shared by
 * the setup wizard's launch step and the live-mode hydration path.
 */
import { listShareholders } from '../../../../../../services/ShareholderApiClient';

/** Map UI exclusion code → API Title-Case enum string. */
export const toApiExclusionType = (value) => {
  if (value === 'dqi') return 'DQI';
  if (value === '318') return 'Section 318';
  return null;
};

/** Map API Title-Case exclusion enum → UI short code. */
export const fromApiExclusionType = (value) => {
  if (value === 'DQI') return 'dqi';
  if (value === 'Section 318') return '318';
  return null;
};

/** Build a local-shape shareholder row from the API row returned by `listShareholders`. */
export const mapApiRowToLocalShareholder = (row) => ({
  id: row.id,
  name: row.name,
  email: row.email || '',
  shares: Number(row.shares) || 0,
  excluded: !!row.excluded,
  exclusionType: fromApiExclusionType(row.exclusionType),
  attributedTo: null,
  relationship: row.section318Relationship || null,
  importedFromFileName: row.importedFromFileName || null,
  emailPrecheckStatus: row.emailPrecheckStatus || null,
  emailPrecheckSentAt: row.emailPrecheckSentAt || null,
  emailPrecheckMessageId: row.emailPrecheckMessageId || null,
  emailPrecheckError: row.emailPrecheckError || null,
});

/**
 * Add the per-shareholder voting fields (`pct`, `status`, `signedAt`, `docViews`) so the live admin
 * dashboard can render. Shared by `SetupPage.handleLaunch` and `buildLiveCapTableFromApiRows`.
 */
export const prepareEligibleForVoting = (eligible, totalShares) =>
  eligible.map((sh) => ({
    ...sh,
    pct: totalShares > 0 ? sh.shares / totalShares : 0,
    status: 'pending',
    signedAt: null,
    docViews: [],
  }));

/** Map a server-returned cap table to the live-mode payload (prepared eligible + totals + excluded). */
export const buildLiveCapTableFromApiRows = (rows) => {
  const all = rows.map(mapApiRowToLocalShareholder);
  const total = all.reduce((sum, s) => sum + s.shares, 0);
  const eligible = all.filter((s) => !s.excluded);
  const excluded = all.filter((s) => s.excluded);
  const eligShares = eligible.reduce((sum, s) => sum + s.shares, 0);
  return { prepared: prepareEligibleForVoting(eligible, total), total, eligShares, excluded };
};

/** Map an API vote decision (`Approve`/`Disapprove`/`Abstain`/null) → live-table status code. */
export const decisionToLocalStatus = (decision) => {
  if (decision === 'Approve') return 'approved';
  if (decision === 'Disapprove') return 'disapproved';
  if (decision === 'Abstain') return 'abstained';
  return 'pending';
};

/**
 * Overlay the server vote tally onto the prepared cap table so the live dashboard shows real
 * per-shareholder status and signed timestamps instead of simulated `pending` rows. Returns the
 * same `{ prepared, total, eligShares, excluded }` shape with `prepared` updated in place.
 */
export const applyTallyToCapTable = (capTable, tally) => {
  if (!tally || !Array.isArray(tally.shareholders)) return capTable;
  const byId = new Map(tally.shareholders.map((s) => [s.shareholderId, s]));
  const prepared = capTable.prepared.map((sh) => {
    const t = byId.get(sh.id);
    if (!t) return sh;
    return { ...sh, status: decisionToLocalStatus(t.decision), signedAt: t.recordedAt || null };
  });
  return { ...capTable, prepared };
};

/**
 * Dedupe concurrent `listShareholders` calls for a given analysis id. The cache entry is removed
 * once the in-flight request settles, so a subsequent mount triggers a fresh fetch.
 */
const shareholderLoadByAnalysisId = new Map();

export const loadShareholdersOnce = async (analysisId) => {
  const existing = shareholderLoadByAnalysisId.get(analysisId);
  if (existing) return existing;
  const req = listShareholders(analysisId).finally(() => {
    shareholderLoadByAnalysisId.delete(analysisId);
  });
  shareholderLoadByAnalysisId.set(analysisId, req);
  return req;
};
